/**
 * The records behind one data-quality finding.
 *
 * The tab answers HOW MANY; this answers WHICH. It is a right-side sheet from the
 * design system (`components/ui/sheet`, Radix Dialog underneath), so Escape, the
 * overlay click, the focus trap, the focus return and the labelled dialog role are
 * the primitive's behaviour rather than something re-implemented here. Same
 * primitive and same shape as `BexioWarningDetailDrawer`, which solved this exact
 * problem for the reconciliation warnings.
 *
 * ONE DRAWER FOR ALL FINDINGS.
 * It takes a resolved descriptor — label, description, ref family, items — rather
 * than reaching into the response itself. The section owns which finding is open;
 * this owns how a record of that shape is rendered. That is also why the
 * unattributed-invoice hint uses this drawer instead of a second special-purpose
 * one: two drawers for two lists of the same ref family is two places for the
 * count and the copy to drift.
 *
 * COUNT VS. RENDERED ROWS — the distinction that matters.
 * The header always states the FULL count, the footer says shown-of-total, and a
 * button reveals more. The header number is never the number currently rendered.
 *
 * TWO SOURCES FOR THAT LIST, AND THE DIFFERENCE IS WHERE THE NEXT ROWS COME FROM.
 * Almost every finding arrives with its records IN FULL inside the analytics
 * response; those are held in `detail.items` and the cap is purely a DOM one
 * (`PAGE` at a time), because rendering dozens of cards on open costs work no
 * reader asked for.
 *
 * One finding is different. The provider-missing set has no upper bound and is
 * deliberately NOT carried in the analytics response — only its count is — so its
 * records are read from the paginated invoice endpoint instead. For that shape the
 * caller passes `remote`, and the drawer renders what the reader already holds:
 * there is no `slice` on that path, because the page boundary is the server's.
 * `detail.count` stays the authority for "how many", and when the server's own
 * total disagrees with it the drawer says so rather than picking a winner.
 *
 * READ-ONLY, AND STRUCTURALLY SO.
 * No mutation and no write control. This component itself performs no request
 * either — `remote` is state it is handed, not state it fetches, which keeps the
 * rendering testable without a network and keeps the one request that does exist in
 * a single place. The only interactive elements are "more rows", a retry after a
 * failed read, and — for invoice-shaped records — a link to the one Bexio surface
 * that exists. There is no route for a single Bexio invoice, so no record links to
 * itself, and `/admin/invoices` is a different entity entirely and never a target.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import {
  familyShowsInvoices,
  nestedSpec,
  refTitle,
  visibleRefFields,
} from "./revenueQualityFields";

/** Rows rendered before the reader asks for more. Same cap as the Bexio drawer. */
export const PAGE = 20;

/** The one existing surface for mirrored invoices. Never the legacy table. */
export const BEXIO_INVOICES_ROUTE = "/admin/bexio/invoices";

/**
 * A stable-enough key per record.
 *
 * The ref families carry different identifiers and none is guaranteed unique
 * across a list (a tenancy can appear twice in `multiple_rent_rows`), so the index
 * is part of the key rather than a fallback for it.
 */
function rowKey(item, i) {
  const id =
    item?.bexio_payment_id ??
    item?.request_id ??
    item?.revenue_row_id ??
    item?.first_revenue_row_id ??
    item?.bexio_invoice_id ??
    item?.tenancy_id ??
    "row";
  return `${id}-${i}`;
}

function FieldGrid({ family, item }) {
  const fields = visibleRefFields(family, item);
  if (!fields.length) return null;
  return (
    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
      {fields.map((f) => (
        <div key={f.label} className="min-w-0">
          <dt className="truncate text-xs text-muted-foreground">{f.label}</dt>
          <dd className="truncate text-sm tabular-nums text-foreground">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function RecordCard({ family, item }) {
  const nested = nestedSpec(family);
  const children = nested && Array.isArray(item?.[nested.key]) ? item[nested.key] : [];

  return (
    <li className="rounded-lg border border-border p-3">
      <span className="block truncate text-sm font-semibold text-foreground">
        {refTitle(family, item)}
      </span>
      <FieldGrid family={family} item={item} />

      {children.length ? (
        <div className="mt-3 border-t border-border/60 pt-2">
          <p className="text-xs font-medium text-muted-foreground">{nested.label}</p>
          {/* Every claiming row, none of them marked as the correct one: the
              database guarantees no ordering between them, so picking a winner
              would be this layer inventing one. */}
          <ul className="mt-1 space-y-2">
            {children.map((child, ci) => (
              <li key={rowKey(child, ci)} className="rounded-md bg-muted/40 p-2">
                <span className="block truncate text-xs font-medium text-foreground">
                  {refTitle(nested.family, child)}
                </span>
                <FieldGrid family={nested.family} item={child} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

export default function RevenueQualityDetailDrawer({ open, onOpenChange, detail, remote }) {
  const [shown, setShown] = useState(PAGE);

  const family = detail?.family;
  // Which of the two sources this finding uses. Decided by the DESCRIPTOR, not by
  // whether a `remote` prop happens to be present: the drawer holds one `remote`
  // for the whole section, and a finding that carries its own records must keep
  // reading them even while that state exists beside it.
  const isRemote = detail?.remote === true;
  const localRows = Array.isArray(detail?.items) ? detail.items : [];
  const remoteRows = Array.isArray(remote?.rows) ? remote.rows : [];
  const rows = isRemote ? remoteRows : localRows;

  // A different finding in the same drawer starts at the top again.
  useEffect(() => {
    setShown(PAGE);
  }, [detail?.key, open]);

  // The server already decided where this page ends, so there is nothing to cut.
  const visible = isRemote ? rows : rows.slice(0, shown);
  const remaining = rows.length - visible.length;

  // HOW MANY, from the source that owns that question. For a remote finding the
  // analytics figure is the count the reader saw on the tab, and it is what the
  // header must repeat — falling back to the list's own total only while the first
  // page is still in flight and no count was handed over.
  const listTotal = isRemote ? (remote?.total ?? null) : rows.length;
  const headerCount = isRemote ? (detail?.count ?? listTotal ?? 0) : rows.length;
  const footerTotal = listTotal ?? headerCount;

  // The two numbers describe the same persistent state — `provider_missing_at IS
  // NOT NULL` — read twice, moments apart. A disagreement is therefore real
  // information: either the set changed between the two reads, or one of them is
  // stale. Neither is this component's to resolve, and quietly showing whichever is
  // larger would hide the only evidence that anything is off.
  const inconsistent =
    isRemote && listTotal != null && detail?.count != null && listTotal !== detail.count;

  const loading = isRemote && remote?.loading === true;
  const error = isRemote ? remote?.error || null : null;
  const canLoadMore = isRemote ? remote?.hasMore === true : remaining > 0;
  const onMore = isRemote ? remote?.loadMore : () => setShown((n) => n + PAGE);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Wider than the primitive's default `sm:max-w-sm`: these records carry
          four to seven values each, and 384px turns every one into three lines.
          Flex column so the header stays put and only the list scrolls. */}
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="shrink-0 border-b border-border p-5 pr-12 text-left">
          <SheetTitle className="text-base">{detail?.label || "Hinweis"}</SheetTitle>
          <SheetDescription>
            {/* The authoritative count, never what is rendered. */}
            {headerCount} {headerCount === 1 ? "betroffener Eintrag" : "betroffene Einträge"}
            {/* The population, because three groups publish a finding under the
                same label over three different sets. */}
            {detail?.context ? ` · ${detail.context}` : ""}
          </SheetDescription>
          {detail?.description ? (
            <p className="mt-1 text-xs text-muted-foreground">{detail.description}</p>
          ) : null}
        </SheetHeader>

        {/* Stated ABOVE the rows: a reader who scrolls a partial list should not
            have to reach the footer to learn that the two counts disagree. */}
        {inconsistent ? (
          <p
            role="status"
            className="shrink-0 border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
          >
            Der Kennzahlen-Wert nennt {detail.count}, die Rechnungsliste {listTotal}. Beide lesen
            denselben gespeicherten Zustand — die Abweichung bedeutet, dass sich der Bestand
            zwischen den beiden Abfragen geändert hat oder eine der beiden Antworten älter ist.
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto p-5" aria-busy={loading || undefined}>
          {/* Order matters. A failed read is reported as a failure even when the
              list is empty, because "keine Einzeldetails" would then be a claim
              about the data made on the strength of a request that never arrived. */}
          {error ? (
            <div className="text-sm text-foreground">
              <p>{error}</p>
              <button
                type="button"
                onClick={remote?.reload}
                className="mt-2 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Erneut versuchen
              </button>
            </div>
          ) : loading && rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Rechnungen werden geladen …</p>
          ) : rows.length === 0 ? (
            // Fail-safe, not a placeholder record. A finding whose count is
            // positive while its array is empty is a contract surprise, and the
            // honest answer is to say so rather than to render an invented row.
            <p className="text-sm text-muted-foreground">
              Für diesen Hinweis sind keine Einzeldetails verfügbar.
            </p>
          ) : (
            <ul className="space-y-3">
              {visible.map((item, i) => (
                <RecordCard key={rowKey(item, i)} family={family} item={item} />
              ))}
            </ul>
          )}
        </div>

        {rows.length > 0 ? (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border p-3 text-xs text-muted-foreground">
            <span>
              {visible.length} von {footerTotal} angezeigt
            </span>
            <span className="flex items-center gap-3">
              {familyShowsInvoices(family) ? (
                <Link
                  to={BEXIO_INVOICES_ROUTE}
                  className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Bexio-Rechnungen öffnen →
                </Link>
              ) : null}
              {canLoadMore ? (
                <button
                  type="button"
                  onClick={onMore}
                  disabled={loading || undefined}
                  className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                >
                  {loading ? "Wird geladen …" : "Weitere anzeigen"}
                </button>
              ) : null}
            </span>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
